// Combo tracker. Each kill inside a 10-second window of the previous one
// ticks the combo multiplier up the ladder, capped at x3. Falling off the
// timer resets to neutral. At x3 the player becomes a SERVER-WIDE BOUNTY —
// visible marker on all clients, and whoever kills them earns bonus XP.

export const COMBO = {
  WINDOW_MS: 10_000,
  TIERS: [1.0, 1.2, 1.4, 1.6, 1.8, 2.0, 3.0] as const,
  BOUNTY_TIER: 6,              // index into TIERS — the x3 bounty state
  BOUNTY_KILL_BONUS_MULT: 2.0, // killing a bountied player = +2x run XP
} as const;

export type ComboSnap = {
  tier: number;                // 0..TIERS.length-1
  mult: number;                // TIERS[tier]
  remainingMs: number;         // ms until the window expires
  bounty: boolean;             // tier >= BOUNTY_TIER
};

export function makeCombo(): ComboTracker {
  return new ComboTracker();
}

export class ComboTracker {
  private tier = 0;
  private expiresAtMs = 0;

  // Register a kill at `nowMs`. Returns the new tier so callers can emit
  // visuals / score bumps when the ladder advances.
  kill(nowMs: number): { tier: number; mult: number; bounty: boolean; climbed: boolean } {
    const active = nowMs < this.expiresAtMs;
    const prevTier = this.tier;
    if (active) {
      this.tier = Math.min(COMBO.TIERS.length - 1, this.tier + 1);
    } else {
      this.tier = 1;
    }
    this.expiresAtMs = nowMs + COMBO.WINDOW_MS;
    return {
      tier: this.tier,
      mult: COMBO.TIERS[this.tier],
      bounty: this.tier >= COMBO.BOUNTY_TIER,
      climbed: this.tier > prevTier,
    };
  }

  // Called every tick to decay the combo when the 10s window runs out.
  tick(nowMs: number): { fellOff: boolean } {
    if (this.tier > 0 && nowMs >= this.expiresAtMs) {
      this.tier = 0;
      this.expiresAtMs = 0;
      return { fellOff: true };
    }
    return { fellOff: false };
  }

  // Reset on death / match restart — the combo is a persistent state the
  // caller chooses to clear in specific events.
  reset() {
    this.tier = 0;
    this.expiresAtMs = 0;
  }

  snapshot(nowMs: number): ComboSnap {
    const remainingMs = this.tier > 0 ? Math.max(0, this.expiresAtMs - nowMs) : 0;
    return {
      tier: this.tier,
      mult: COMBO.TIERS[this.tier] ?? 1,
      remainingMs,
      bounty: this.tier >= COMBO.BOUNTY_TIER,
    };
  }

  get current(): number {
    return COMBO.TIERS[this.tier] ?? 1;
  }
}
